import { DndContext } from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faBars, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Divider, Popover, Tooltip } from "antd";
import { arrayOf, bool, func, node, shape, string } from "prop-types";
import { useIntl } from "react-intl";

const SortableWidgetRow = ({ widget, title, onToggleVisible }) => {
  const intl = useIntl();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: widget.id });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className="flex items-center gap-2 py-1.5 px-1 aria-pressed:relative aria-pressed:z-[999]"
      {...attributes}
    >
      <button
        type="button"
        className="cursor-grab touch-none border-0 bg-transparent p-1 text-gray-500 hover:text-gray-800"
        data-test-id={`dashboard-widget-drag-${widget.id}`}
        aria-label={intl.formatMessage({ id: "dashboard.customize.drag" })}
        {...listeners}
      >
        <FontAwesomeIcon icon={faBars} />
      </button>
      <Tooltip
        title={intl.formatMessage({
          id: widget.visible
            ? "dashboard.customize.hide"
            : "dashboard.customize.show",
        })}
      >
        <button
          type="button"
          className="border-0 bg-transparent p-1 text-gray-500 hover:text-gray-800"
          data-test-id={`dashboard-widget-visible-${widget.id}`}
          aria-label={intl.formatMessage({
            id: widget.visible
              ? "dashboard.customize.hide"
              : "dashboard.customize.show",
          })}
          onClick={() => onToggleVisible(widget.id, !widget.visible)}
        >
          <FontAwesomeIcon icon={widget.visible ? faEye : faEyeSlash} />
        </button>
      </Tooltip>
      <span
        className={`flex-1 truncate text-sm ${
          widget.visible ? "text-gray-900" : "text-gray-400"
        }`}
      >
        {title}
      </span>
    </li>
  );
};

SortableWidgetRow.propTypes = {
  widget: shape({
    id: string.isRequired,
    visible: bool.isRequired,
  }).isRequired,
  title: string.isRequired,
  onToggleVisible: func.isRequired,
};

const CustomizeMenuContent = ({ widgets, setOrder, setVisible, reset }) => {
  const intl = useIntl();
  const keys = widgets.map(({ id }) => id);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = keys.indexOf(active.id.toString());
    const newIndex = keys.indexOf(over.id.toString());
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = [...widgets];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setOrder(reordered);
  };

  return (
    <div className="w-72" data-test-id="dashboard-customize-menu">
      <div className="flex flex-wrap gap-2 pb-2">
        <Button size="small" data-test-id="dashboard-customize-reset" onClick={reset}>
          {intl.formatMessage({ id: "dashboard.customize.reset" })}
        </Button>
      </div>
      <Divider className="my-2" />
      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext items={keys} strategy={verticalListSortingStrategy}>
          <ul className="m-0 max-h-72 list-none overflow-y-auto p-0">
            {widgets.map((widget) => (
              <SortableWidgetRow
                key={widget.id}
                widget={widget}
                title={intl.formatMessage({ id: widget.titleId })}
                onToggleVisible={setVisible}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};

CustomizeMenuContent.propTypes = {
  widgets: arrayOf(
    shape({
      id: string.isRequired,
      titleId: string.isRequired,
      visible: bool.isRequired,
    }),
  ).isRequired,
  setOrder: func.isRequired,
  setVisible: func.isRequired,
  reset: func.isRequired,
};

const DashboardCustomizeMenu = ({
  open,
  onOpenChange,
  widgets,
  setOrder,
  setVisible,
  reset,
  children,
}) => (
  <Popover
    trigger="click"
    placement="bottomRight"
    open={open}
    onOpenChange={onOpenChange}
    content={
      <CustomizeMenuContent
        widgets={widgets}
        setOrder={setOrder}
        setVisible={setVisible}
        reset={reset}
      />
    }
  >
    {children}
  </Popover>
);

DashboardCustomizeMenu.propTypes = {
  open: bool.isRequired,
  onOpenChange: func.isRequired,
  widgets: arrayOf(
    shape({
      id: string.isRequired,
      titleId: string.isRequired,
      visible: bool.isRequired,
    }),
  ).isRequired,
  setOrder: func.isRequired,
  setVisible: func.isRequired,
  reset: func.isRequired,
  children: node.isRequired,
};

export default DashboardCustomizeMenu;
